import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { DateTile, formatNtd } from './ui';
import { CATEGORY_LABELS } from './CategoryTabs';
import { IconBell, IconChevronRight } from './Icons';

export interface PortalEvent {
  id: string;
  title: string;
  titleEn?: string;
  subtitle?: string;
  bannerImageUrl?: string;
  thumbnailImageUrl?: string;
  eventDate?: string;
  registrationDeadline?: string;
  location?: string;
  category?: string;
  minPrice?: number;
  maxPrice?: number;
  bookingCount?: number;
  maxParticipants?: number;
  registrationStatus: 'open' | 'closing_soon' | 'closed' | 'full';
  daysUntilDeadline?: number | null;
  isFeatured?: boolean;
  theme?: { primaryColor: string };
}

interface EventCardProps {
  event: PortalEvent;
  variant?: 'featured' | 'row';
}

function deadlineText(e: PortalEvent): string | null {
  if (e.registrationStatus === 'closed') return '已截止';
  if (e.registrationStatus === 'full') return '已額滿';
  if (e.daysUntilDeadline != null) {
    if (e.daysUntilDeadline <= 0) return '今天截止';
    if (e.daysUntilDeadline <= 7) return `剩 ${e.daysUntilDeadline} 天`;
  }
  if (e.registrationDeadline) {
    const d = new Date(e.registrationDeadline);
    if (!isNaN(d.getTime())) return `${d.getMonth() + 1}/${d.getDate()} 截止`;
  }
  return null;
}

function metaLine(e: PortalEvent): string {
  const parts: string[] = [];
  if (e.category && CATEGORY_LABELS[e.category]) parts.push(CATEGORY_LABELS[e.category]);
  if (e.subtitle) parts.push(e.subtitle);
  else if (e.location) parts.push(e.location);
  const dl = deadlineText(e);
  if (dl) parts.push(dl);
  return parts.join(' · ');
}

/** Lighten a hex color toward white; used for the featured card tint. */
export function tint(hex: string, amount = 0.88): string {
  const h = hex.replace('#', '');
  if (h.length !== 6) return '#F2F4F7';
  const n = parseInt(h, 16);
  const mix = (c: number) => Math.round(c + (255 - c) * amount);
  const r = mix(n >> 16), g = mix((n >> 8) & 255), b = mix(n & 255);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

export default function EventCard({ event, variant = 'row' }: EventCardProps) {
  const color = event.theme?.primaryColor || '#2E4467';
  const closed = event.registrationStatus === 'closed' || event.registrationStatus === 'full';
  const image = event.thumbnailImageUrl || event.bannerImageUrl;

  if (variant === 'featured') {
    return (
      <Link
        to={`/event/${event.id}`}
        className="flex flex-col gap-3.5 p-4 rounded-card text-ink"
        style={{ background: tint(color) }}
      >
        {event.bannerImageUrl && (
          <img src={event.bannerImageUrl} alt="" className="w-full aspect-[2/1] object-cover rounded-tile" />
        )}
        <div className="flex items-start gap-3.5">
          <DateTile date={event.eventDate} color={color} />
          <div className="flex-1 min-w-0 flex flex-col gap-1">
            <span className="text-lg font-black leading-snug">{event.title}</span>
            <span className="text-[13px] text-muted">{metaLine(event)}</span>
            {event.bookingCount != null && event.bookingCount > 0 && (
              <span className="text-xs text-muted tabular">{event.bookingCount} 人已報名</span>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between">
          <span className="flex items-baseline gap-1">
            <span className="text-xs text-muted">團報價起</span>
            <span className="tabular text-lg font-black" style={{ color }}>{formatNtd(event.minPrice)}</span>
          </span>
          <span
            className="h-10 px-[18px] rounded-full text-white text-sm font-bold inline-flex items-center"
            style={{ background: closed ? '#9AA3AF' : color }}
          >
            {closed ? (event.registrationStatus === 'full' ? '已額滿' : '已截止') : '開始報名'}
          </span>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/event/${event.id}`}
      className={clsx(
        'flex items-center gap-3.5 px-4 py-3.5 rounded-card text-ink',
        closed ? 'border-[1.5px] border-dashed border-line' : 'bg-soft',
      )}
    >
      {image ? (
        <img src={image} alt="" className="w-14 h-14 shrink-0 rounded-tile object-cover" />
      ) : (
        <DateTile date={event.eventDate} color={color} muted={closed} />
      )}
      <div className="flex-1 min-w-0 flex flex-col gap-0.5">
        <span className={clsx('text-base font-bold leading-snug line-clamp-2', closed && 'text-ink')}>{event.title}</span>
        <span className="text-xs text-muted line-clamp-1">{metaLine(event)}</span>
      </div>
      {closed ? (
        <IconChevronRight size={18} className="text-faint shrink-0" />
      ) : event.minPrice != null ? (
        <span className="tabular text-[15px] font-black shrink-0">{formatNtd(event.minPrice)}</span>
      ) : (
        <IconChevronRight size={18} className="text-faint shrink-0" />
      )}
    </Link>
  );
}

/** Row for an event that is not open yet. */
export function ComingSoonRow({ title, date }: { title: string; date?: string }) {
  return (
    <div className="flex items-center gap-3.5 px-4 py-3.5 rounded-card border-[1.5px] border-dashed border-line text-muted">
      <DateTile date={date} muted />
      <div className="flex-1 min-w-0 flex flex-col gap-0.5">
        <span className="text-base font-bold text-ink">{title}</span>
        <span className="text-xs">即將開放</span>
      </div>
      <IconBell size={20} />
    </div>
  );
}
